import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ShoppingList } from '@/types/api';

interface ShoppingListState {
  // Current shopping list
  currentList: ShoppingList | null;
  
  // Checked items by ingredient name
  checkedItems: Record<string, boolean>;
  
  // Custom items added by user
  customItems: Array<{
    id: string;
    ingredient: string;
    quantity?: number;
    unit?: string;
    aisle?: string;
    checked: boolean;
  }>;
  
  // Actions
  setShoppingList: (list: ShoppingList) => void;
  toggleItemChecked: (ingredient: string) => void;
  setItemChecked: (ingredient: string, checked: boolean) => void;
  addCustomItem: (item: { ingredient: string; quantity?: number; unit?: string; aisle?: string; }) => void;
  removeCustomItem: (itemId: string) => void;
  toggleCustomItemChecked: (itemId: string) => void;
  clearCheckedItems: () => void;
  clearCustomItems: () => void;
  resetList: () => void;
  
  // Computed
  getCheckedCount: () => number;
  getTotalCount: () => number;
  getCompletionPercentage: () => number;
}

export const useShoppingListStore = create<ShoppingListState>()(
  persist(
    (set, get) => ({
      currentList: null,
      checkedItems: {},
      customItems: [],

      setShoppingList: (list: ShoppingList) => {
        set({ currentList: list });
      },

      toggleItemChecked: (ingredient: string) => {
        set((state) => ({
          checkedItems: {
            ...state.checkedItems,
            [ingredient]: !state.checkedItems[ingredient]
          }
        }));
      },

      setItemChecked: (ingredient: string, checked: boolean) => {
        set((state) => ({
          checkedItems: {
            ...state.checkedItems,
            [ingredient]: checked
          }
        }));
      },

      addCustomItem: (item) => {
        const customItem = {
          id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ingredient: item.ingredient,
          quantity: item.quantity,
          unit: item.unit,
          aisle: item.aisle || 'otros',
          checked: false
        };
        
        set((state) => ({
          customItems: [...state.customItems, customItem]
        }));
      },

      removeCustomItem: (itemId: string) => {
        set((state) => ({
          customItems: state.customItems.filter(item => item.id !== itemId)
        }));
      },

      toggleCustomItemChecked: (itemId: string) => {
        set((state) => ({
          customItems: state.customItems.map(item => 
            item.id === itemId 
              ? { ...item, checked: !item.checked }
              : item
          )
        }));
      },

      clearCheckedItems: () => {
        set({ checkedItems: {} });
      },

      clearCustomItems: () => {
        set({ customItems: [] });
      },

      resetList: () => {
        set({ 
          currentList: null,
          checkedItems: {},
          customItems: []
        });
      },

      getCheckedCount: () => {
        const state = get();
        const listItemsChecked = Object.values(state.checkedItems).filter(Boolean).length;
        const customItemsChecked = state.customItems.filter(item => item.checked).length;
        return listItemsChecked + customItemsChecked;
      },

      getTotalCount: () => {
        const state = get();
        const listItemsCount = state.currentList?.items.length || 0;
        const customItemsCount = state.customItems.length;
        return listItemsCount + customItemsCount;
      },

      getCompletionPercentage: () => {
        const state = get();
        const total = state.getTotalCount();
        const checked = state.getCheckedCount();
        return total > 0 ? Math.round((checked / total) * 100) : 0;
      }
    }),
    {
      name: 'shopping-list-storage',
      // Only persist checked items and custom items, not the full list
      partialize: (state) => ({
        checkedItems: state.checkedItems,
        customItems: state.customItems
      })
    }
  )
);
